import axios from 'axios'
import { jwtDecode } from 'jwt-decode'

import type { RundownTokenPayload } from '~shared/rundown/permission'

import { getUser } from './firebase.js'

// oxlint-disable oxc/no-async-endpoint-handlers
import type { InternalAxiosRequestConfig } from 'axios'

/** Decoded rundown token: our claims plus the standard `exp` `jwt.sign` adds. */
type DecodedRundownToken = RundownTokenPayload & { exp?: number }

// Module-level caching
let decodedToken: string | null = null
let decodedPayload: DecodedRundownToken | null = null

/**
 * Cache: Decodes the rundown token once per token string.
 *
 * Introduced to prevent the heavy jwtDecode being called in every render and
 * request throughout the app.
 *
 * @remarks
 *   The payload of a given token is immutable, so caching by the token string
 *   itself is enough: a new token has a new string and misses the cache.
 */
function decodeToken(token: string): DecodedRundownToken {
  if (decodedToken === token && decodedPayload) return decodedPayload

  decodedPayload = jwtDecode<DecodedRundownToken>(token)
  decodedToken = token

  return decodedPayload
}

/**
 * Per-tab client session id. Tab-scoped via sessionStorage so it persists
 * across hot reloads and refreshes within a tab, but each new tab gets its own
 * id. Sent as `X-Client-Session` header on all requests so server-side logs can
 * distinguish writes from concurrent browser windows.
 */
const CLIENT_SESSION_KEY = 'rs.clientSession'
let cachedClientSessionId: string | null = null
function getClientSessionId(): string {
  if (cachedClientSessionId) return cachedClientSessionId
  try {
    let id = sessionStorage.getItem(CLIENT_SESSION_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(CLIENT_SESSION_KEY, id)
    }
    cachedClientSessionId = id
  } catch {
    cachedClientSessionId = crypto.randomUUID()
  }
  return cachedClientSessionId
}

/**
 * Instantiate Axios instances
 */
// Use default options for instances,
// since axios doesn't support instance chaining
export const DEFAULT_AXIOS_OPTIONS = { timeout: 30000 }
export const baseApi = axios.create(DEFAULT_AXIOS_OPTIONS)
export const rundownApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_RUNDOWN_FUNCTION_BASE,
})
export const runnerApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_RUNNER_FUNCTION_BASE,
})
/**
 * @deprecated - This API client is moved to src/entities/team/api/client.ts
 * Import teamApi from there instead
 */
export const teamApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_TEAM_FUNCTION_BASE,
})
export const auxApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_AUX_FUNCTION_BASE,
})
export const billingApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_BILLING_FUNCTION_BASE,
})
export const eventApi = axios.create({
  ...DEFAULT_AXIOS_OPTIONS,
  baseURL: import.meta.env.VITE_EVENT_FUNCTION_BASE,
})

/**
 * Add request interceptor to attach the firebase auth token.
 *
 * user.getIdToken(): Returns the current token if it has not expired or if it
 * will not expire in the next five minutes. Otherwise, this will refresh the
 * token and return a new one. See:
 *
 * @see https://firebase.google.com/docs/reference/js/auth.user.md#usergetidtoken
 */
async function useAuth(config: InternalAxiosRequestConfig) {
  const user = await getUser()
  if (user) {
    config.headers['Authorization'] = await user.getIdToken()
  }
  return config
}
baseApi.interceptors.request.use(useAuth)
rundownApi.interceptors.request.use(useAuth)
runnerApi.interceptors.request.use(useAuth)
teamApi.interceptors.request.use(useAuth)
auxApi.interceptors.request.use(useAuth)
billingApi.interceptors.request.use(useAuth)
eventApi.interceptors.request.use(useAuth)

function useClientSession(config: InternalAxiosRequestConfig) {
  config.headers['X-Client-Session'] = getClientSessionId()
  return config
}
baseApi.interceptors.request.use(useClientSession)
rundownApi.interceptors.request.use(useClientSession)
runnerApi.interceptors.request.use(useClientSession)
teamApi.interceptors.request.use(useClientSession)
auxApi.interceptors.request.use(useClientSession)
billingApi.interceptors.request.use(useClientSession)
eventApi.interceptors.request.use(useClientSession)

/**
 * Store and handle rundown token
 */
export const RundownToken = {
  rundownId: null as string | null,
  href: null as string | null,
  signature: null as string | null,
  token: null as string | null,
  /** In-flight token request, shared by concurrent callers of getToken(). */
  pending: null as Promise<void> | null,

  /**
   * Request a new rundown token
   *
   * @private
   *
   * @returns {Promise<void>}
   */
  async requestToken() {
    if (!this.rundownId) {
      throw new Error('Missing parameter to get rundown token: rundownId')
    }

    const { data } = await rundownApi.post(
      `/${this.rundownId}/create-token`,
      {
        href: this.href,
        signature: this.signature,
      },
      // @ts-expect-error TODO: Type correctly, since this is additional param
      { skipToken: true },
    )

    this.token = data.token
  },

  /**
   * Get a valid token, requesting a new one if necessary.
   *
   * Concurrent callers share a single in-flight request, so parallel API calls
   * at page load do not each POST create-token.
   *
   * @returns {string} - A JWT token
   *
   * @public
   */
  async getToken() {
    if (this.isValid) return this.token

    if (!this.pending) {
      this.pending = this.requestToken().finally(() => {
        this.pending = null
      })
    }
    await this.pending

    return this.token
  },

  /**
   * Set parameters for token requests.
   *
   * @returns {void}
   *
   * @public
   */
  setParameters(rundownId: string, href: string, signature: string | null) {
    this.rundownId = rundownId
    this.href = href
    this.signature = signature
    this.token = null // Reset token to force a new request
    this.pending = null
  },

  /**
   * Forget the current rundown entirely. Called when leaving a rundown page so
   * a stale token never leaks into the next rundown.
   *
   * @public
   */
  reset() {
    this.rundownId = null
    this.href = null
    this.signature = null
    this.token = null
    this.pending = null
  },

  /**
   * Check if the current token is valid.
   * Consider invalid if within 5 minutes of expiration.
   *
   * @public
   */
  get isValid(): boolean {
    if (!this.token) return false
    const decoded = decodeToken(this.token)
    const exp = new Date((decoded.exp ?? 1) * 1000)
    return exp > new Date(Date.now() + 5 * 60 * 1000)
  },

  /**
   * Get the rundown access level of this token.
   *
   * @public
   */
  get access() {
    if (!this.token) return false

    const decoded = decodeToken(this.token)
    return decoded.access
  },

  get permissions(): number | undefined {
    if (!this.token) return undefined

    const decoded = decodeToken(this.token)

    return decoded.permissions
  },
}

/**
 * Add request interceptor for rundown token.
 * Skip token for requests with skipToken option to avoid invinite loops.
 */
async function useRundownToken(config: InternalAxiosRequestConfig) {
  // @ts-expect-error TODO: Type correctly
  if (config.skipToken) return config

  if (RundownToken.rundownId) {
    config.headers['Rundown-Token'] = await RundownToken.getToken()
  }
  return config
}
rundownApi.interceptors.request.use(useRundownToken)
runnerApi.interceptors.request.use(useRundownToken)
